import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SavedPaymentMethodsData, savedPaymentMethodsKey } from '../paymentApi';
import { PaymentRemoveBankAccountError, requestRemoveBankAccount } from './bankAccountApi';

interface UseRemoveBankAccountOptions {
  onSuccess?: () => void;
}

/**
 * Removes a saved bank account. No optimistic update — the cache is only reconciled once the
 * mutation resolves, then invalidated so the list refetches from the server.
 */
export function useRemoveBankAccount({ onSuccess }: UseRemoveBankAccountOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => requestRemoveBankAccount(id),
    onSuccess: (removedAccount) => {
      queryClient.setQueryData<SavedPaymentMethodsData>(savedPaymentMethodsKey, (existing) => ({
        cards: existing?.cards ?? [],
        bankAccounts: (existing?.bankAccounts ?? []).filter((account) => account.id !== removedAccount.id),
      }));
      void queryClient.invalidateQueries({ queryKey: savedPaymentMethodsKey });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      // PaymentRemoveBankAccountError wraps removeBankAccount's Failure variant (NOT_FOUND, DEFAULT_ACCOUNT).
      const code = error instanceof PaymentRemoveBankAccountError ? error.code : extractGqlErrorCode(error);
      if (error instanceof Error) {
        trackException(error, { source: 'removeBankAccount', errorCode: code });
      }
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return { submit: (id: string) => mutation.mutate(id), isPending: mutation.isPending };
}
